import React from "react";

import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import type { ServiceStatusVariant } from "kontrolEdge/helpers/serviceStatusTag";

interface ServiceStatusValueProps {
  /** Wording already decided by `resolveServiceStatusTag`. */
  label: string;
  /** Severity from the same call. Drives the colour, and nothing else does. */
  variant: ServiceStatusVariant;
  /** The backend's explanation, when it sent one. */
  tooltip?: string;
}

/**
 * How a service's status looks in a detail card: a tag, in the colour the
 * shared status helper decided, optionally explaining itself on hover.
 *
 * It is a component rather than four ternaries because there are four detail
 * cards — Port, Internet, Cloud Connectivity, Datacenter Interconnect — and
 * each had grown its own chain of them. That is how Cloud Connectivity became
 * the only one whose status row rendered with no colour at all: nothing forced
 * the four to agree, so they stopped agreeing, and nobody noticed until a
 * customer did.
 *
 * A tag rather than coloured text on purpose. The service lists paint their
 * Status column with `GenericTag` and the same variant, so using the same
 * primitive here is what makes "the detail says what the list said" true by
 * construction instead of by two mappings that have to be kept in step.
 */
export default function ServiceStatusValue({
  label,
  variant,
  tooltip,
}: ServiceStatusValueProps) {
  const tag = <GenericTag label={label} variant={variant} size="small" />;

  if (!tooltip) return tag;

  return (
    <GenericTooltip
      text={tooltip}
      position="bottom"
      horizontalAlign="right"
      width="20rem"
    >
      {tag}
    </GenericTooltip>
  );
}
